import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import * as React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { isDashboardChromeEmbed } from '../chromeEmbed';
import { ONBOARDING_PATH, useOnboardingStatus } from './OnboardingStatus';
import type { OnboardingState } from './types';

/** One line on where the onboarding stands, in the client's words. */
export function bannerText(s: OnboardingState | null): string {
    if (!s) {
        return 'Your account setup is in progress.';
    }
    switch (s.go_live.status) {
        case 'requested':
            return "You've asked to go live — we'll let you know.";
        case 'approved':
        case 'running':
            return 'Your account is going live.';
        case 'rejected':
            return 'Your go-live request needs another look — see the note on the Onboarding page.';
        default:
            break;
    }
    switch (s.phase) {
        case 'setup':
            return "We're setting up your account.";
        case 'choose':
            return 'Choose the tours to start with.';
        case 'importing':
            return 'Your tours are being imported.';
        case 'ready':
            return 'Your account is ready — request go-live when you are.';
        default:
            return 'Your account setup is in progress.';
    }
}

/** Shown above every page but the Onboarding page while the onboarding is open: the rest of the
 *  dashboard stays usable (plan §16, Q5), this line leads back. */
const OnboardingBanner = () => {
    const { status, state } = useOnboardingStatus();
    const { pathname } = useLocation();
    if (status !== 'open' || pathname.startsWith(ONBOARDING_PATH) || isDashboardChromeEmbed()) {
        return null;
    }
    return (
        <Alert
            severity="info"
            icon={<RocketLaunchOutlinedIcon fontSize="inherit" />}
            data-testid="onboarding-banner"
            sx={{ borderRadius: 0, py: 0, alignItems: 'center' }}
            action={(
                <Button component={RouterLink} to={ONBOARDING_PATH} color="inherit" size="small">
                    Open onboarding
                </Button>
            )}
        >
            {bannerText(state)}
        </Alert>
    );
};

/** The landing page while it waits (at most LANDING_WAIT_MS) to know whether to open the Onboarding
 *  page instead. */
export const LandingHold = () => (
    <Box data-testid="onboarding-landing-hold" sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} aria-label="Loading" />
    </Box>
);

export default OnboardingBanner;
